import { Box } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, ART_DES_ANTRAGS, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { Field, FormStrucItem } from 'src/lib/types/types';
import {
	EmailValidation,
	GetMinMaxDecimalValidation,
	NonRequired,
	PhoneNumberValidation,
	PostalCodeValidation,
	Required,
} from 'src/lib/validation/ValidationTypes';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { getInstitutionsTypOption } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung, isOrgaAbteilungEinerHochschule, isTrue } from 'src/utils/helper';

// import Schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
let P = Schema.attributes;

const AntragstellerSkizzeLoewe: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;
	return (
		<>
			<ApplicationHeading title='Organisation' description={PLATZHALTER_TEXT} />

			<Box>
				<Grid2 container spacing={6}>
					<Grid2 sm={6}>
						{/**
						 * *************************************************************************************************************************
						 * ************************** Organisation Basis Daten (Die sind immer unabhängig vom Programm da) *************************
						 * *************************************************************************************************************************
						 */}
						<CreateForm
							formStruc={formStrucOrgaBasic(application)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={isFormantrag ? true : updatedReadOnly}
						/>
					</Grid2>
					<Grid2 sm={6}>
						{/**
						 * *************************************************************************************************************************
						 * ***************************** Organisation Daten hier sind aber immer vom Programm abhängig *****************************
						 * *************************************************************************************************************************
						 */}
						<CreateForm
							formStruc={formStrucOrgaSpalte2(application)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={isFormantrag ? true : updatedReadOnly}
						/>
					</Grid2>
				</Grid2>
			</Box>
		</>
	);
};

export default AntragstellerSkizzeLoewe;

const CUSTOM_PLACEHOLDER = 'n.n. - bitte im Profil angeben';

const formStrucOrgaBasic = (application: any): FormStrucItem[] => {
	let struc: FormStrucItem[] = [];

	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);

	const orgaDaten = get(application, P.Antragstellende_Organisation.path, {});
	const istHochschule = isOrgaAbteilungEinerHochschule(orgaDaten);

	if (istHochschule) {
		struc = [
			{
				fields: [
					{
						...P.Antragstellende_Organisation.schema.attributes.Hochschule.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation,
						label: 'Name der Hochschule',
						disabled: true,
						fieldGroupWidth: '70%',
					},
					{
						...P.Antragstellende_Organisation.schema.attributes.Hochschule.schema.attributes.Geschaeftsbezogene_Daten_Kuerzel,
						label: 'Kürzel',
						disabled: true,
						fieldGroupWidth: '30%',
					},
				],
			},
			{
				fields: [
					{ ...P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation, label: 'Fachbereich', disabled: true },
					{ ...P.Antragstellende_Organisation_Strasse_Und_Hausnummer, disabled: true },
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},

			{
				fields: [
					{
						...P.Antragstellende_Organisation_PLZ,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
					},
					{
						...P.Antragstellende_Organisation_Ort,
						disabled: true,
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
					},
				],
				userProps: { columnToDivide: 2 },
			},
			{
				fields: [
					{
						...P.Antragstellende_Organisation_Telefonnummer,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: PhoneNumberValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
					},
					{
						...P.Antragstellende_Organisation_E_Mail_Adresse,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: EmailValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
					},
				],
				userProps: { columnToDivide: 2 },
			},
		];
	} else {
		const adressZusatz = get(application, P.Antragstellende_Organisation_Adresszusatz.path, null);
		const hatKeinAbweichendenSitzList = [ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit];
		struc = [
			{
				fields: [
					{ ...P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation, disabled: true },
					{
						...P.Antragstellende_Organisation_Rechtsform_Der_Organisation,
						sortAlphabetically: true,
						disabled: true,
						placeholder: CUSTOM_PLACEHOLDER,
					},
					{ ...P.Antragstellende_Organisation_Strasse_Und_Hausnummer, disabled: true },
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
			...(adressZusatz ?
				[
					{
						fields: [{ ...P.Antragstellende_Organisation_Adresszusatz, disabled: true }],
						userProps: { direction: 'column', columnToDivide: 1 },
					},
				]
			:	[]),
			{
				fields: [
					{
						...P.Antragstellende_Organisation_PLZ,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
					},
					{
						...P.Antragstellende_Organisation_Ort,
						disabled: true,
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
					},
				],
				userProps: { columnToDivide: 2 },
			},
			...(!hatKeinAbweichendenSitzList.includes(antragsFormular) ?
				[
					{
						fields: [
							{
								...P.Antragstellende_Organisation_Abweichender_Sitz,
								...(antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium && { label: 'Abweichender Sitz des Unternehmens' }),
							},
						],
						userProps: { columnToDivide: 1 },
					},
				]
			:	[]),
			...(isTrue(get(application, P.Antragstellende_Organisation_Abweichender_Sitz.path)) ?
				[
					{
						fields: [
							{
								...P.Antragstellende_Organisa_0234235106_resse_Strasse_Und_Hausnummer,
								label: 'Abweichende Straße und Hausnummer',
							},
						],
						userProps: { columnToDivide: 1 },
					},
				]
			:	[]),
			...(isTrue(get(application, P.Antragstellende_Organisation_Abweichender_Sitz.path)) ?
				[
					{
						fields: [
							{
								...P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_PLZ,
								label: 'Abweichende PLZ',
								type: 'integer',
								maxDigit: 5,
								validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
								fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
							},
							{
								...P.Antragstellende_Organisation_Abweichender_Sitz_Adresse_Ort,
								label: 'Abweichender Ort',
								fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
							},
						],
					},
				]
			:	[]),
			{
				fields: [
					{
						...P.Antragstellende_Organisation_Telefonnummer,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: PhoneNumberValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '30%' : null,
					},
					{
						...P.Antragstellende_Organisation_E_Mail_Adresse,
						disabled: true,
						validation: [{ validateFunction: Required }, { validateFunction: EmailValidation }],
						fieldGroupWidth: antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium ? '70%' : null,
					},
				],
				userProps: { columnToDivide: 2 },
			},
		];
	}

	return struc;
};
const formStrucOrgaSpalte2 = (application: any) => {
	let struc: FormStrucItem[] = [];

	const orgaDaten = get(application, P.Antragstellende_Organisation.path, {});
	const istHochschule = isOrgaAbteilungEinerHochschule(orgaDaten);

	if (istHochschule) {
		struc = [
			{
				fields: [
					{
						...P.Organisation_Antragsberechtigt_Als,
						type: FORM_FIELDS_TYPES.singleSelect,
						sortAlphabetically: false,
						options: getInstitutionsTypOption(application),
					},
				],
				userProps: { columnToDivide: 1 },
			},
			{
				fields: [
					{
						...P.Antragstellende_Organisation_Relevante_Kernkompentenz,
						label: 'Relevante Kernkompetenz',
					},
				],
				userProps: { columnToDivide: 1 },
			},
		];
	} else {
		struc = [
			{
				fields: [
					{
						...P.Organisation_Antragsberechtigt_Als,
						type: FORM_FIELDS_TYPES.singleSelect,
						sortAlphabetically: false,
						options: getInstitutionsTypOption(application),
					},
				],
				userProps: { columnToDivide: 1 },
			},
			{
				fields: [
					{ ...P.Antragstellende_Organisation_Anzahl_Der_Mitarbeiter, label: 'Anzahl Mitarbeitende' },
					{ ...P.Antragstellende_Organisation_Jahresumsatzin },
				],
				userProps: { columnToDivide: 2 },
			},
			{
				fields: [
					{ ...P.Antragstellende_Organisation_Branche },
					{
						...P.Antragstellende_Organisation_FE_Anteil,
						label: 'F&E-Anteil (% vom Umsatz)',
						validation: [{ validateFunction: GetMinMaxDecimalValidation(0, 100) }],
					},
				],
				userProps: { columnToDivide: 2 },
			},
			{
				fields: [P.Antragstellende_Organisation_Relevante_Kernkompentenz],
				userProps: { columnToDivide: 1 },
			},
			{
				fields: [...getSteuerNummerOrUmsatzsteuerIdStruc(application), { ...P.Antragstellende_Organisation_Gruendungsdatum, disabled: true }],
				userProps: { columnToDivide: 2 },
			},
			{
				fields: [
					{
						...P.Antragstellende_Organisation_Handelsregisternummer,
						label: 'Registernummer',
						tooltip: '',
						disabled: true,
						validation: [{ validateFunction: NonRequired }],
					},
				],
				userProps: { columnToDivide: 2, columnGap: '1rem' },
			},
			{
				fields: [{ ...P.Antragstellende_Organisation_Landkreis, disabled: true, sortAlphabetically: true, placeholder: CUSTOM_PLACEHOLDER }],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];
	}

	return struc;
};
const getSteuerNummerOrUmsatzsteuerIdStruc = (application: any) => {
	const hasSteuerNummer = !!get(application, P.Antragstellende_Organisation_Steuernummer.path);
	const hasUmsatzsteuerId = !!get(application, P.Antragstellende_Organisation_U_St_Id_Nr.path);
	let fieldArr: Field[] = [];
	if (hasSteuerNummer) {
		fieldArr.push({ ...P.Antragstellende_Organisation_Steuernummer, disabled: true });
	} else if (hasUmsatzsteuerId) {
		fieldArr.push({ ...P.Antragstellende_Organisation_U_St_Id_Nr, disabled: true });
	}
	return fieldArr;
};

export function getFormStrucAntragstellerSkizzeLoewe(application: any) {
	let tableStruc: any = [];

	let orgaStruc: FormStrucItem[] = [];

	if (get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Antrag) {
		orgaStruc = concat(tableStruc, formStrucOrgaBasic(application), formStrucOrgaSpalte2(application));
	} else {
		orgaStruc = concat(tableStruc, formStrucOrgaBasic(application), formStrucOrgaSpalte2(application));
	}

	if (
		get(application, P.Antragstellende_Pruefung_Hinweis_Fuer_Den_Antragsteller.path) &&
		!isTrue(get(application, P.Antragstellende_Pruefung_Erledigt.path))
	) {
		return concat(orgaStruc);
	} else return orgaStruc;
}
